import { useRef } from 'react';
import {
    Controller,
    type Control,
    type FieldValues,
    type Path,
    type PathValue
} from 'react-hook-form';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useValidationMessage } from '../i18n/validationMessages';
import { useFormReadOnly } from './formReadOnly';
import { completedTimeOnBlur, type TimeFieldRef } from './timeFieldAutoMinutes';

/**
 * A time-of-day input bound to a react-hook-form field that stores an 'HH:mm'
 * string (empty = unset). Uses the MUI X TimePicker — not the native time input
 * — so it renders 24h German style like every other picker (adapter locale
 * pinned to 'de').
 *
 * Typing just the hour and tabbing on fills the minutes with 00 — see
 * completedTimeOnBlur.
 */
export default function TimeStringField<T extends FieldValues>({
    control,
    name,
    label,
    maxTime,
    warning = false,
    disabled = false
}: {
    control: Control<T>;
    name: Path<T>;
    label: string;
    /** Latest selectable time (e.g. now, when the day is today). */
    maxTime?: Dayjs;
    /**
     * Mark the field amber — the value is legal input but worth a second look
     * (e.g. it puts the workday past the ArbZG ceiling). MUI has no `warning`
     * counterpart to `error`, so this is its documented stand-in: the warning
     * palette plus a forced `focused`. A real `error` outranks it.
     */
    warning?: boolean;
    disabled?: boolean;
}) {
    const validationMessage = useValidationMessage();
    const fieldRef = useRef<TimeFieldRef>(null);
    // Follows the surrounding form (see formReadOnly).
    const readOnly = useFormReadOnly();
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => {
                const showWarning = warning && !fieldState.error;
                return (
                    <TimePicker
                        ampm={false}
                        maxTime={maxTime}
                        disabled={disabled}
                        readOnly={readOnly}
                        label={label}
                        value={
                            field.value
                                ? dayjs(`2000-01-01T${field.value as string}`)
                                : null
                        }
                        onChange={(next) =>
                            field.onChange(
                                next && next.isValid()
                                    ? next.format('HH:mm')
                                    : ''
                            )
                        }
                        slotProps={{
                            // The handle on to what is typed in each section —
                            // the only way to see a lone hour (see below).
                            field: { fieldRef },
                            textField: {
                                inputRef: field.ref,
                                // The picker's field doesn't inherit the
                                // MuiTextField filled default — set it here.
                                variant: 'filled',
                                fullWidth: true,
                                onBlur: () => {
                                    field.onBlur();
                                    // An hour typed without minutes becomes the
                                    // full hour; anything else is left alone.
                                    const completed = completedTimeOnBlur(
                                        fieldRef.current
                                    );
                                    if (completed) {
                                        field.onChange(
                                            completed as PathValue<T, Path<T>>
                                        );
                                    }
                                },
                                error: !!fieldState.error,
                                ...(showWarning
                                    ? { color: 'warning', focused: true }
                                    : {}),
                                helperText: fieldState.error
                                    ? validationMessage(
                                          fieldState.error.message
                                      )
                                    : undefined
                            }
                        }}
                    />
                );
            }}
        />
    );
}
